/**
 * StagedCampaignDialog — create a chart-driven STAGED SCALE-IN campaign for options.
 *
 * Pick a view + N price levels (E1..EN, against the thesis) + ONE expiry (evaluated across
 * candidates) + a hard stop. The app deploys a small first tranche now and adds larger,
 * freshly re-optimised tranches (same locked expiry) as price reaches each level (doubling
 * ¼·¼·½). This dialog shows a COMBINED "all tranches" payoff plus each tranche's full legs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
  MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, Alert, CircularProgress,
  Chip, ToggleButton, ToggleButtonGroup, Tabs, Tab, Divider, IconButton, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { optionsService } from '../../services/options.service';
import PayoffChartPro from '../charts/PayoffChartPro';
import { thetaVerdict, deltaVerdict, vegaVerdict } from './campaignGreeks';
import { syntheticSymbolsService } from '../../services/syntheticSymbols.service';

const VIEWS = [
  { v: 'WONT_FALL_BELOW', label: "Won't fall below", danger: 'down' as const, two: false },
  { v: 'WONT_RISE_ABOVE', label: "Won't rise above", danger: 'up' as const, two: false },
  { v: 'WILL_BREAK_ABOVE', label: 'Will break above', danger: 'down' as const, two: false },
  { v: 'WILL_BREAK_BELOW', label: 'Will break below', danger: 'up' as const, two: false },
  { v: 'STAY_IN_RANGE', label: 'Will stay in range', danger: 'down' as const, two: true },
  { v: 'WILL_BREACH', label: 'Break out either side', danger: 'down' as const, two: true },
];
const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const fmtSigned = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}`;
const fmtPx = (v: number) => Math.round(v).toLocaleString('en-IN');
const dteLabel = (d: number) => d < 1 ? `${Math.round(d * 24)}h` : `${Math.round(d)}d`;
const expLabel = (iso?: string) => iso ? new Date(iso + 'T00:00:00Z').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' }) : '—';

/** Combined-view behaviour explainer, per view. */
const combinedNote = (view: string): string => {
  if (view === 'WONT_FALL_BELOW' || view === 'WONT_RISE_ABOVE')
    return 'If price tests your levels and then holds the view, every tranche profits together — the combined max profit is the sum. Your ₹ SL amount caps the total loss; the danger level only alerts you to adjust.';
  if (view === 'WILL_BREAK_ABOVE' || view === 'WILL_BREAK_BELOW')
    return 'Each level lets you add the directional bet more cheaply; if the move finally comes, all tranches gain together.';
  if (view === 'STAY_IN_RANGE')
    return '⚠️ Each tranche re-centres to a different price, so they don’t all win together — the combined curve shows the true aggregate. Best when price settles mid-range.';
  return 'Defined risk — the combined max loss is the total premium paid; a big move either way pays across the tranches.';
};

/** Group identical legs and multiply by lots → { side, type, strike, expiry, entry, qty }. */
const groupLegs = (legs: any[], lots: number) => {
  const m = new Map<string, any>();
  for (const l of legs || []) {
    const k = `${l.action}|${l.optionType}|${l.strike}|${l.expiry}`;
    const g = m.get(k);
    if (g) g.count++; else m.set(k, { action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry, entry: l.ltp, count: 1 });
  }
  return Array.from(m.values()).sort((a, b) => b.strike - a.strike).map(g => ({ ...g, qty: g.count * lots }));
};
/** Compact legs table — Side · Option · Strike · Qty · Expiry · Entry ₹ (muted for carried). */
const LegsTable: React.FC<{ legs: any[]; muted?: boolean }> = ({ legs, muted }) => (
  <Table size="small" sx={{ mb: 0.5, opacity: muted ? 0.72 : 1, '& td, & th': { py: 0.25, px: 1, borderColor: muted ? 'transparent' : undefined } }}>
    <TableHead><TableRow>
      <TableCell sx={{ fontWeight: 700 }}>Side</TableCell>
      <TableCell sx={{ fontWeight: 700 }}>Option</TableCell>
      <TableCell sx={{ fontWeight: 700 }} align="right">Strike</TableCell>
      <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
      <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
      <TableCell sx={{ fontWeight: 700 }} align="right">Entry ₹</TableCell>
    </TableRow></TableHead>
    <TableBody>
      {legs.map((l: any, i: number) => (
        <TableRow key={i}>
          <TableCell><Chip size="small" label={l.action === 'BUY' ? 'BUY' : 'SELL'} sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700, bgcolor: l.action === 'BUY' ? '#e8f5e9' : '#ffebee', color: l.action === 'BUY' ? '#1b5e20' : '#b71c1c' }} /></TableCell>
          <TableCell>{l.optionType}</TableCell>
          <TableCell align="right">{fmtPx(l.strike)}</TableCell>
          <TableCell align="right">{l.qty}</TableCell>
          <TableCell>{expLabel(String(l.expiry).slice(0, 10))}</TableCell>
          <TableCell align="right">{l.entry != null ? Number(l.entry).toFixed(2) : '—'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

type Seed = { underlyingSymbol: string; view?: string; viewLevel?: number; viewLevel2?: number; spot?: number; stageLevels?: number[]; hardSl?: number; hardSlUpper?: number; ladder?: boolean; entries?: number[]; upLevels?: number[]; dnLevels?: number[] };

const StagedCampaignDialog: React.FC<{ open: boolean; onClose: () => void; seed?: Seed; onCreated?: () => void }> = ({ open, onClose, seed, onCreated }) => {
  const [symbol, setSymbol] = useState(seed?.underlyingSymbol || 'BTCUSD');
  const [view, setView] = useState(seed?.view || 'WONT_FALL_BELOW');
  const [spot, setSpot] = useState<number>(seed?.spot || 0);
  const [n, setN] = useState(seed?.stageLevels?.length || 3);
  const [rule, setRule] = useState<'doubling' | 'equal'>('doubling');
  const [levels, setLevels] = useState<number[]>(seed?.stageLevels?.slice() || []);
  const [viewLevel, setViewLevel] = useState<number>(seed?.viewLevel || 0);
  const [viewLevel2, setViewLevel2] = useState<number>(seed?.viewLevel2 || 0);
  const [hardSl, setHardSl] = useState<number>(seed?.hardSl || 0);
  const [hardSlUpper, setHardSlUpper] = useState<number>(seed?.hardSlUpper || 0);
  const [slPct, setSlPct] = useState<number>(65);      // SL amount = slPct% of aggregate MAX LOSS
  const [tpPct, setTpPct] = useState<number>(50);      // TP amount = tpPct% of achievable max profit
  const [investment, setInvestment] = useState<number | ''>('');   // per-campaign override (blank = instrument default)
  const [leverage, setLeverage] = useState<number | ''>('');
  const [sizeTouched, setSizeTouched] = useState(false);
  // LIVE-default (item 8): manual campaign creation targets the real account.
  const [mode, setMode] = useState<'PAPER' | 'LIVE'>('LIVE');
  const [expiry, setExpiry] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState(0); // 0 = Combined, 1..N = stages
  const [savingStage, setSavingStage] = useState<number | null>(null);

  /* Save ONE tranche's spread as a synthetic symbol. A staged campaign is a SEQUENCE of
     distinct spreads (each stage re-anchored to its own level), so it can't be a single
     synthetic — but every stage IS a concrete fixed basket, so each can be saved and then
     charted / triggered on its own from the Charts symbol pane. No order placed. */
  const kindToStrategyId: Record<string, string> = {
    CREDIT_PUT: 'bull_put_spread', CREDIT_CALL: 'bear_call_spread',
    DEBIT_CALL: 'bull_call_spread', DEBIT_PUT: 'bear_put_spread',
  };
  const kindToView: Record<string, 'UP' | 'DOWN'> = {
    CREDIT_PUT: 'UP', DEBIT_CALL: 'UP', CREDIT_CALL: 'DOWN', DEBIT_PUT: 'DOWN',
  };
  const saveStageAsSymbol = async (stage: any) => {
    const b = stage?.basket;
    const legs = (b?.legs || []).map((l: any) => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), expiry: String(l.expiry || expiry).slice(0, 10) }));
    if (!legs.length) { toast.error('This tranche has no priced legs yet.'); return; }
    setSavingStage(stage.index);
    const r = await syntheticSymbolsService.create({
      underlyingSymbol: symbol,
      strategyId: stage.kind ? kindToStrategyId[stage.kind] : undefined,
      legs,
      netPremium: Number(b?.netPremium) || 0,
      viewAligned: stage.kind ? kindToView[stage.kind] : undefined,
    });
    setSavingStage(null);
    if (r.ok) {
      toast.success(`💾 Tranche saved as synthetic symbol “${r.name}” — find it in the Charts symbol pane.`, { duration: 7000, id: 'synth-save' });
      try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
    } else {
      toast.error(r.reason || 'Could not save this tranche as a symbol', { id: 'synth-save' });
    }
  };

  const metaV = useMemo(() => VIEWS.find(v => v.v === view)!, [view]);
  const danger = metaV.danger, twoLevel = metaV.two, noHardStop = view === 'WILL_BREACH';
  // Campaigns ALWAYS use the anchored-ladder engine. `drawnLadder` = levels drawn on the chart
  // (sent verbatim); otherwise MANUAL mode (Options hub) builds the ladder from the dialog inputs:
  // directional = the E1..EN + danger fields; range/breakout = N levels each side derived from the
  // range bounds (editable via the fields; danger/profit levels are derived server-side).
  const drawnLadder = !!seed?.ladder;
  const isLadder = true;
  const ladderBody = () => {
    if (drawnLadder) return { ladder: true, entries: seed?.entries || [], upLevels: seed?.upLevels || [], dnLevels: seed?.dnLevels || [] };
    if (view === 'WONT_FALL_BELOW' || view === 'WONT_RISE_ABOVE') return { ladder: true, entries: levels.filter(x => x > 0) };
    const step = Math.max(1, Math.round((spot || levels[0] || 1) * 0.01));
    const up = Array.from({ length: n }, (_, k) => Math.round((viewLevel2 || spot) + (k + 1) * step));
    const dn = Array.from({ length: n }, (_, k) => Math.round((viewLevel || spot) - (k + 1) * step));
    return { ladder: true, upLevels: up, dnLevels: dn };
  };

  const seedLevels = useCallback((sp: number) => {
    if (!(sp > 0)) return;
    const step = sp * 0.013, arr: number[] = [];
    for (let i = 0; i < n; i++) arr.push(danger === 'up' ? Math.round(sp + i * step) : Math.round(sp - i * step));
    setLevels(arr);
    setHardSl(noHardStop ? 0 : (danger === 'up' ? Math.round(sp + (n + 0.5) * step) : Math.round(sp - (n + 0.5) * step)));
    if (twoLevel) { setViewLevel(Math.round(sp - step * 2.2)); setViewLevel2(Math.round(sp + step * 2.2)); }
    else setViewLevel(danger === 'up' ? Math.round(sp + step * 0.6) : Math.round(sp - step * 0.6));
  }, [n, danger, twoLevel, noHardStop]);

  const skipAutoSeedRef = useRef(false);
  // Default the number of stages from Settings (entry-split count, 2–5) — same as non-option
  // trades. Skipped when levels were drawn on the chart (their count already dictates N).
  useEffect(() => {
    if (!open || seed?.stageLevels?.length) return;
    api.get<any>('/user/settings', { headers: { _suppressToast: 'true' } })
      .then((r: any) => { const nn = Number(r?.data?.data?.entrySplitCount); if (Number.isFinite(nn)) setN(Math.max(2, Math.min(5, nn))); })
      .catch(() => {});
  }, [open]); // eslint-disable-line
  useEffect(() => {
    if (!open) return;
    // Sync the underlying from the seed first (a fresh chart/draw may target a different one).
    if (seed?.underlyingSymbol && seed.underlyingSymbol.toUpperCase() !== symbol) { setSymbol(seed.underlyingSymbol.toUpperCase()); return; }
    let cancelled = false; setExpiry(undefined);
    (async () => {
      let sp = seed?.spot || 0;
      if (!(sp > 0)) { try { const r: any = await optionsService.getOptionProfile(symbol, 'D'); sp = Number(r?.data?.data?.spot) || 0; } catch { /* */ } }
      if (cancelled) return; setSpot(sp);
      // Levels DRAWN on the chart take priority over the auto-seed from spot.
      if (seed?.stageLevels?.length) {
        skipAutoSeedRef.current = true;
        setN(seed.stageLevels.length);
        setLevels(seed.stageLevels.slice());
        if (seed.hardSl && seed.hardSl > 0) setHardSl(seed.hardSl);
        if (seed.hardSlUpper && seed.hardSlUpper > 0) setHardSlUpper(seed.hardSlUpper);
        if (seed.viewLevel2 && seed.viewLevel2 > 0) setViewLevel2(seed.viewLevel2);
        setViewLevel(seed.viewLevel && seed.viewLevel > 0 ? seed.viewLevel : seed.stageLevels[0]);
      } else {
        seedLevels(sp);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, symbol]);
  // Re-seed from spot when the user changes stage count / view — UNLESS we just applied
  // chart-drawn levels (that setN would otherwise clobber the drawn levels).
  useEffect(() => {
    if (skipAutoSeedRef.current) { skipAutoSeedRef.current = false; return; }
    if (spot > 0) { seedLevels(spot); setExpiry(undefined); }
  }, [n, view]); // eslint-disable-line

  useEffect(() => {
    if (!open || !(levels.length >= 1)) return;
    if (!isLadder && !noHardStop && !(hardSl > 0)) return;
    if (!isLadder && view === 'STAY_IN_RANGE' && !(hardSlUpper > 0)) return;
    const t = setTimeout(async () => {
      setLoading(true); setErr(null);
      try {
        const body: any = { underlyingSymbol: symbol, view, viewLevel, stageLevels: levels, hardSlPrice: hardSl, sizingRule: rule, slPct: slPct / 100, tpPct: tpPct / 100, ...ladderBody() };
        if (twoLevel) body.viewLevel2 = viewLevel2;
        if (view === 'STAY_IN_RANGE' && !isLadder) body.hardSlPriceUpper = hardSlUpper;
        if (expiry) body.expiryDate = expiry;
        if (sizeTouched && investment !== '' && Number(investment) > 0) body.investmentAmount = Number(investment);
        if (sizeTouched && leverage !== '' && Number(leverage) > 0) body.leverage = Number(leverage);
        const r: any = await optionsService.previewCampaign(body);
        const d = r?.data?.data || null; setPreview(d);
        if (d?.spot) setSpot(d.spot);
        if (d?.expiryDate && !expiry) setExpiry(d.expiryDate);
        // Prefill the sizing inputs from the instrument defaults until the user edits them.
        if (!sizeTouched) { if (d?.investmentAmount != null) setInvestment(Math.round(d.investmentAmount)); if (d?.leverage != null) setLeverage(d.leverage); }
      } catch (e: any) { setErr(e?.response?.data?.error?.message || 'Could not build a preview'); setPreview(null); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, symbol, view, JSON.stringify(levels), hardSl, hardSlUpper, viewLevel, viewLevel2, rule, expiry, slPct, tpPct, sizeTouched, investment, leverage]);

  const setLevel = (i: number, val: number) => setLevels(ls => ls.map((l, j) => j === i ? val : l));

  const place = async () => {
    setPlacing(true);
    try {
      const body: any = {
        underlyingSymbol: symbol, view, viewLevel, stageLevels: levels, hardSlPrice: hardSl, sizingRule: rule, mode, expiryDate: expiry,
        slPct: slPct / 100, tpPct: tpPct / 100,
        slAmountInr: Math.round(preview?.slAmountInr || 0), tpAmountInr: Math.round(preview?.tpAmountInr || 0),
        ...ladderBody(),
      };
      if (twoLevel) body.viewLevel2 = viewLevel2;
      if (view === 'STAY_IN_RANGE' && !isLadder) body.hardSlPriceUpper = hardSlUpper;
      if (sizeTouched && investment !== '' && Number(investment) > 0) body.investmentAmount = Number(investment);
      if (sizeTouched && leverage !== '' && Number(leverage) > 0) body.leverage = Number(leverage);
      const r: any = await optionsService.createCampaign(body);
      toast.success(`Staged campaign #${r?.data?.data?.campaignId} placed (${mode}) — stage 1 deployed, rest armed at your levels.`, { duration: 8000 });
      onCreated?.(); onClose();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Could not place the campaign', { duration: 9000 }); }
    finally { setPlacing(false); }
  };

  const stages: any[] = preview?.stages || [];
  const isCredit = preview?.isCredit;
  const candidates: any[] = preview?.expiryCandidates || [];
  const cum: any[] = preview?.cumulative || [];
  const tabIdx = Math.min(tab, Math.max(0, stages.length - 1));        // per-stage cumulative tab (0-based)
  const sel = stages[tabIdx];                                          // the stage whose NEW legs this tab adds
  const cumSel = cum[tabIdx] || null;                                  // cumulative position through this stage
  const carriedLegs = stages.slice(0, tabIdx).flatMap((s: any) => groupLegs(s.basket?.legs || [], s.estLots));
  const newLegs = groupLegs(sel?.basket?.legs || [], sel?.estLots || 1);
  // Cumulative payoff is already ₹-total (combinePayoff × lots).
  const chartBasket = cumSel?.payoffData ? { payoffData: cumSel.payoffData, maxProfit: cumSel.maxProfit, maxLoss: cumSel.maxLoss, breakEvens: cumSel.breakEvens, legs: [] } : null;
  const chartSpot = sel?.index === 1 ? spot : sel?.triggerPrice;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        Staged scale-in · {symbol}
        {isCredit != null && <Chip size="small" label={isCredit ? 'Credit view' : 'Debit view'} color={isCredit ? 'success' : 'info'} sx={{ height: 20 }} />}
        {expiry && <Chip size="small" variant="outlined" label={`Expiry ${expLabel(expiry)}`} sx={{ height: 20 }} />}
        <IconButton size="small" onClick={onClose} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Deploy a small first tranche now and add larger, freshly re-optimised tranches (same expiry) as price reaches your levels —
          all under a ₹ SL amount that closes every tranche; a danger level alerts you to adjust before that. The app evaluates strikes and expiries and sizes each tranche.
        </Typography>

        {/* Inputs */}
        <Stack direction="row" spacing={1.2} sx={{ flexWrap: 'wrap', rowGap: 1.2, mb: 1.2, alignItems: 'center' }}>
          <TextField size="small" label="Symbol" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} sx={{ width: 120 }} />
          <TextField size="small" select label="View" value={view} onChange={e => setView(e.target.value)} sx={{ width: 200 }}>
            {VIEWS.map(v => <MenuItem key={v.v} value={v.v}>{v.label}</MenuItem>)}
          </TextField>
          {!drawnLadder && <TextField size="small" select label="Levels/side" value={n} onChange={e => setN(Number(e.target.value))} sx={{ width: 110 }}>
            {[2, 3, 4, 5].map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>}
          {!isLadder && <ToggleButtonGroup size="small" exclusive value={rule} onChange={(_, v) => v && setRule(v)}>
            <ToggleButton value="doubling" sx={{ textTransform: 'none', px: 1.2 }}>¼·¼·½</ToggleButton>
            <ToggleButton value="equal" sx={{ textTransform: 'none', px: 1.2 }}>Equal</ToggleButton>
          </ToggleButtonGroup>}
          <Chip size="small" variant="outlined" label={`Spot ${fmtPx(spot)}`} />
          {drawnLadder ? <Chip size="small" color="secondary" label="Drawn ladder" sx={{ fontWeight: 700 }} /> : <Chip size="small" color="secondary" variant="outlined" label="Manual ladder" sx={{ fontWeight: 700 }} />}
          <Tooltip arrow title="Total capital for THIS campaign (split across the tranches). Defaults to the amount set for this underlying on the Instruments page; edit for this campaign only.">
            <TextField size="small" type="number" label="Investment ₹" value={investment} onChange={e => { setSizeTouched(true); setInvestment(e.target.value === '' ? '' : Number(e.target.value)); }} sx={{ width: 130 }} />
          </Tooltip>
          <Tooltip arrow title="Leverage applied to the investment when sizing lots. Defaults to the underlying's Instruments-page setting; edit for this campaign only.">
            <TextField size="small" type="number" label="Leverage ×" value={leverage} onChange={e => { setSizeTouched(true); setLeverage(e.target.value === '' ? '' : Number(e.target.value)); }} sx={{ width: 108 }} />
          </Tooltip>
        </Stack>

        {/* Expiry candidates — how much premium each expiry fetches for the exposure. */}
        {candidates.length > 0 && (
          <Box sx={{ mb: 1.2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.4 }}>Expiry — pick one (all tranches use it)</Typography>
            <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', rowGap: 0.8 }}>
              {candidates.map((c: any) => {
                const on = c.expiryDate === expiry;
                return (
                  <Tooltip key={c.expiryDate} arrow title={`${c.name} · fit ${Math.round(c.fit)}/100 · ${c.netPremium < 0 ? 'credit' : 'debit'} ${Math.abs(c.netPremium).toFixed(1)}/lot`}>
                    <Box onClick={() => setExpiry(c.expiryDate)} sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1.5, border: t => `1.5px solid ${on ? t.palette.primary.main : t.palette.divider}`, bgcolor: on ? t => (t.palette.mode === 'dark' ? 'rgba(25,118,210,.16)' : 'rgba(25,118,210,.08)') : 'transparent', minWidth: 92 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.74rem' }}>{expLabel(c.expiryDate)} <Typography component="span" variant="caption" color="text.secondary">{c.tier}0</Typography></Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{dteLabel(c.dteDays)} · {c.netPremium < 0 ? 'cr' : 'db'} {fmtInr(Math.abs(c.netPremium))}</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Levels — hidden for a drawn LADDER (the levels come from the chart; shown in the table below). */}
        <Stack direction="row" spacing={1.2} sx={{ flexWrap: 'wrap', rowGap: 1.2, mb: 1.5, alignItems: 'center' }}>
          {!drawnLadder && (twoLevel ? <>
            <TextField size="small" type="number" label="Range low" value={viewLevel} onChange={e => setViewLevel(Number(e.target.value))} sx={{ width: 120 }} />
            <TextField size="small" type="number" label="Range high" value={viewLevel2} onChange={e => setViewLevel2(Number(e.target.value))} sx={{ width: 120 }} />
          </> : null)}
          {!drawnLadder && !twoLevel && levels.map((l, i) => (
            <TextField key={i} size="small" type="number" label={`E${i + 1}`} value={l} onChange={e => setLevel(i, Number(e.target.value))} sx={{ width: 100 }} />
          ))}
          {/* Directional (manual) danger — beyond E_N. Range/breakout dangers are derived server-side. */}
          {!drawnLadder && !twoLevel && !noHardStop && (
            <Tooltip arrow title="Danger level — beyond your last entry. The ₹ SL governs the exit; this is the alert/adjust trigger.">
              <TextField size="small" type="number" label="Danger" value={hardSl} onChange={e => setHardSl(Number(e.target.value))} sx={{ width: 110 }} />
            </Tooltip>
          )}
          <Tooltip arrow title={`The BINDING stop: square off EVERY tranche when the total loss reaches this % of the aggregate MAX LOSS (the defined risk) — a consistent, roomier stop than a margin-based one, so a debit breakout isn't stopped too early. The drawn danger level still alerts you to adjust first, and the stop must persist a tick before it fires.${preview?.slAmountInr ? ` ≈ ${fmtInr(preview.slAmountInr)}.` : ''}`}>
            <TextField size="small" type="number" label="SL % of max loss" value={slPct} onChange={e => setSlPct(Math.max(1, Number(e.target.value) || 0))} sx={{ width: 130 }} InputProps={{ sx: { color: '#c62828', fontWeight: 700 } }}
              helperText={preview?.slAmountInr ? `≈ ${fmtInr(preview.slAmountInr)}` : ' '} FormHelperTextProps={{ sx: { mx: 0, color: '#c62828' } }} />
          </Tooltip>
          <Tooltip arrow title={`The BINDING take-profit: book EVERY tranche off when the total profit reaches this % of the achievable MAX PROFIT of the tranches actually deployed. Because it tracks max profit (not margin) it's always reachable — even if only E1 fires — and grows automatically as E2/E3 add.${preview?.tpAmountInr ? ` ≈ ${fmtInr(preview.tpAmountInr)} at full deployment.` : ''}`}>
            <TextField size="small" type="number" label="TP % of max profit" value={tpPct} onChange={e => setTpPct(Math.max(1, Number(e.target.value) || 0))} sx={{ width: 130 }} InputProps={{ sx: { color: '#2e7d32', fontWeight: 700 } }}
              helperText={preview?.tpAmountInr ? `≈ ${fmtInr(preview.tpAmountInr)}` : ' '} FormHelperTextProps={{ sx: { mx: 0, color: '#2e7d32' } }} />
          </Tooltip>
        </Stack>

        {err && <Alert severity="warning" sx={{ my: 1 }}>{err}</Alert>}
        {loading && !preview && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /> <Typography variant="caption">Evaluating strikes & expiries…</Typography></Box>}

        {preview && (
          <Box>
            {/* Summary bar */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 1, mb: 1.2, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,.04)' : '#f6f7f9', alignItems: 'center' }}>
              <Metric label="Budget" value={fmtInr(preview.budgetInr || 0)} />
              <Divider orientation="vertical" flexItem />
              <Metric label="Tranches" value={`${stages.length} · ${stages.map((s: any) => Math.round(s.sizeFraction * 100) + '%').join('·')}`} />
              <Divider orientation="vertical" flexItem />
              <Metric label="Combined max PROFIT" value={fmtInr(preview.aggregateMaxProfitInr || 0)} color="#2e7d32" hint="If ALL tranches deploy and the view holds to expiry." />
              <Divider orientation="vertical" flexItem />
              <Metric label={noHardStop ? 'Max loss (defined)' : 'Defined max LOSS'} value={fmtInr(preview.aggregateMaxLossInr || 0)} color="#c62828" hint={noHardStop ? 'Total premium paid — defined risk.' : 'The worst case if all tranches deploy and go to expiry — your ₹ SL amount is the actual exit before that.'} />
            </Box>

            {isLadder ? (() => {
              const g = preview.greeks || {}; const meta = preview.ladderMeta || {};
              const tv = thetaVerdict(g.thetaInrPerDay || 0), dv = deltaVerdict(view, g.delta || 0), vv = vegaVerdict(view, g.vegaInr || 0);
              const up = stages.filter((s: any) => s.side === 'UP'), dn = stages.filter((s: any) => s.side === 'DOWN');
              const dir = stages.filter((s: any) => s.side !== 'UP' && s.side !== 'DOWN');
              const kindLabel = (k: string) => k === 'CREDIT_PUT' ? 'Bull-put · credit' : k === 'CREDIT_CALL' ? 'Bear-call · credit' : k === 'DEBIT_CALL' ? 'Bull-call · debit' : 'Bear-put · debit';
              const strikesOf = (b: any) => { const s = (b?.legs || []).map((l: any) => Number(l.strike)).sort((a: number, z: number) => a - z); return s.length ? `${fmtPx(s[0])}/${fmtPx(s[s.length - 1])}` : '—'; };
              const LadderTable = ({ title, arr, danger, profit }: { title: string; arr: any[]; danger?: number | null; profit?: number | null }) => arr.length ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mt: 1 }}>{title}</Typography>
                  <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 1 } }}>
                    <TableHead><TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Level</TableCell><TableCell sx={{ fontWeight: 700 }}>Spread</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fires when</TableCell><TableCell sx={{ fontWeight: 700 }} align="center">Strikes</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Lots</TableCell><TableCell sx={{ fontWeight: 700 }} align="right">Max P</TableCell><TableCell sx={{ fontWeight: 700 }} align="right">Max L</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Symbol</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {arr.map((s: any) => (
                        <TableRow key={s.index}>
                          <TableCell sx={{ fontWeight: 700 }}>{fmtPx(s.anchorLevel ?? s.triggerPrice)}</TableCell>
                          <TableCell><Chip size="small" label={kindLabel(s.kind)} sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700, bgcolor: s.kind?.startsWith('CREDIT') ? '#e8f5e9' : '#e3f2fd', color: s.kind?.startsWith('CREDIT') ? '#1b5e20' : '#0d47a1' }} /></TableCell>
                          <TableCell>price {s.triggerDir === 'DIP' ? '≤' : '≥'} {fmtPx(s.triggerPrice)}</TableCell>
                          <TableCell align="center">{strikesOf(s.basket)}</TableCell>
                          <TableCell align="right">{s.estLots}</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmtInr((Number(s.basket?.maxProfit) || 0) * s.estLots)}</TableCell>
                          <TableCell align="right" sx={{ color: '#c62828' }}>{fmtInr(Math.abs((Number(s.basket?.maxLoss) || 0) * s.estLots))}</TableCell>
                          <TableCell align="center">
                            <Tooltip arrow title="Save this tranche's spread as a synthetic symbol (no order placed) — chart it and trigger it from the Charts symbol pane.">
                              <span><IconButton size="small" disabled={savingStage === s.index || !s.basket?.legs?.length} onClick={() => saveStageAsSymbol(s)} sx={{ p: 0.3 }}>💾</IconButton></span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                    {danger != null && <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 700 }}>🛑 Danger {fmtPx(danger)} — SL applies</Typography>}
                    {profit != null && <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>🎯 Profit-close {fmtPx(profit)} — books the basket</Typography>}
                  </Stack>
                </Box>
              ) : null;
              return (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 0.75, mb: 0.5, borderRadius: 1.5, border: t => `1px solid ${t.palette.divider}`, alignItems: 'center' }}>
                    <Metric label="Θ / day" value={fmtSigned(g.thetaInrPerDay || 0)} color={tv.color} hint={`Full-ladder theta. ${tv.note}`} />
                    <Divider orientation="vertical" flexItem />
                    <Metric label="Net Δ" value={(g.delta || 0).toFixed(2)} color={dv.color} hint={dv.note} />
                    <Divider orientation="vertical" flexItem />
                    <Metric label="Vega ₹" value={fmtSigned(g.vegaInr || 0)} color={vv.color} hint={vv.note} />
                    <Divider orientation="vertical" flexItem />
                    <Metric label="ROI" value={`${preview.roiPct || 0}%`} color="#2e7d32" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Nothing deploys now — each spread fires when price reaches its level. Only ONE side usually fires (budget ÷ N per side). 🛑 SL {fmtInr(preview.slAmountInr || 0)} · 🎯 TP tracks max profit.
                  </Typography>
                  <LadderTable title="Ladder" arr={dir} danger={meta.hardSlPrice} />
                  <LadderTable title="Upside ladder ↑" arr={up} danger={view === 'STAY_IN_RANGE' ? meta.hardSlPriceUpper : undefined} profit={meta.profitLevelUp} />
                  <LadderTable title="Downside ladder ↓" arr={dn} danger={view === 'STAY_IN_RANGE' ? meta.hardSlPrice : undefined} profit={meta.profitLevelDn} />
                  {preview.combined?.payoffData && <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Combined payoff (if the whole triggered side fills)</Typography>
                    <PayoffChartPro basket={{ payoffData: preview.combined.payoffData, maxProfit: preview.combined.maxProfit, maxLoss: preview.combined.maxLoss, breakEvens: preview.combined.breakEvens, legs: [] }} spotPrice={spot} underlyingSymbol={(symbol || '').replace('USD', '')} hideHeader />
                  </Box>}
                </Box>
              );
            })() : (<>
            {/* Per-stage CUMULATIVE tabs — position after each stage fires (legacy re-optimise model). */}
            <Tabs value={tabIdx} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 34, mb: 1, '& .MuiTab-root': { minHeight: 34, textTransform: 'none', fontWeight: 700 } }}>
              {stages.map((s: any, i: number) => (
                <Tab key={s.index} label={i === 0 ? 'E1 · now' : i === stages.length - 1 ? `+E${s.index} · full` : `+E${s.index} @${fmtPx(s.triggerPrice)}`} />
              ))}
            </Tabs>

            {sel && cumSel && (
              <Box>
                {/* Greeks / ROI strip — the cumulative position through this stage. Each Greek box is
                    colour-coded green (working FOR the trade) / red (against) with a plain-English tip. */}
                {(() => { const tv = thetaVerdict(cumSel.thetaInrPerDay || 0), dv = deltaVerdict(view, cumSel.delta || 0), vv = vegaVerdict(view, cumSel.vegaInr || 0); return (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 0.75, mb: 1, borderRadius: 1.5, border: t => `1px solid ${t.palette.divider}`, alignItems: 'center' }}>
                  <Metric label="Θ / day" value={fmtSigned(cumSel.thetaInrPerDay || 0)} color={tv.color} hint={`Theta — ₹ the whole position earns (+) or bleeds (−) per day from time decay. ${tv.note}`} />
                  <Divider orientation="vertical" flexItem />
                  <Metric label="Net Δ" value={(cumSel.delta || 0).toFixed(2)} color={dv.color} hint={`Net delta — directional exposure (+ bullish / − bearish). ${dv.note}`} />
                  <Divider orientation="vertical" flexItem />
                  <Metric label="Vega ₹" value={fmtSigned(cumSel.vegaInr || 0)} color={vv.color} hint={`Vega — ₹ P&L for a 1-point rise in implied volatility. ${vv.note}`} />
                  <Divider orientation="vertical" flexItem />
                  <Metric label="ROI" value={`${cumSel.roiPct || 0}%`} color="#2e7d32" hint="Combined max profit ÷ capital deployed so far." />
                  <Divider orientation="vertical" flexItem />
                  <Metric label="Deployed" value={fmtInr(cumSel.deployedInr || 0)} hint="Margin committed once the tranches through this stage are all deployed." />
                </Box>
                ); })()}

                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.3, mb: 0.5, alignItems: 'baseline' }}>
                  <Typography sx={{ fontWeight: 800 }}>{tabIdx === 0 ? 'Stage 1 — deploys now' : `Position after E${sel.index} fires`}</Typography>
                  <Typography variant="caption"><b>Max profit:</b> <span style={{ color: '#2e7d32', fontWeight: 700 }}>{fmtInr(cumSel.maxProfit || 0)}</span></Typography>
                  <Typography variant="caption"><b>Max loss:</b> <span style={{ color: '#c62828', fontWeight: 700 }}>{fmtInr(Math.abs(cumSel.maxLoss || 0))}</span></Typography>
                  {(cumSel.breakEvens || []).length > 0 && <Typography variant="caption"><b>Break-even:</b> {cumSel.breakEvens.map((b: number) => fmtPx(b)).join(' / ')}</Typography>}
                </Stack>
                {/* Realistic TP for THIS scenario (if only these tranches ever deploy) — TP tracks
                    max profit so it's always reachable; SL is the fixed full-campaign stop. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  🎯 If only {tabIdx === 0 ? 'E1' : `E1–E${sel.index}`} ever deploys, take-profit books at{' '}
                  <b style={{ color: '#2e7d32' }}>{fmtInr(cumSel.tpAmountInr || 0)}</b> ({tpPct}% of this position's {fmtInr(cumSel.maxProfit || 0)} max profit).
                  {tabIdx === stages.length - 1 && <> 🛑 Stop-loss (whole campaign) at <b style={{ color: '#c62828' }}>{fmtInr(preview.slAmountInr || 0)}</b>.</>}
                </Typography>

                {/* Carried (already-open) legs from earlier tranches. */}
                {carriedLegs.length > 0 && <>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 0.5 }}>
                    Already open (from earlier tranches) · projected P&L at {fmtPx(sel.triggerPrice)}:{' '}
                    <span style={{ color: (cumSel.carriedPnl || 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 800 }}>{fmtSigned(cumSel.carriedPnl || 0)}</span>
                  </Typography>
                  <LegsTable legs={carriedLegs} muted />
                </>}

                {/* New legs added at this stage. */}
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: carriedLegs.length ? 0.75 : 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {tabIdx === 0 ? 'Legs' : `New at E${sel.index}`} · {sel.basket?.name} ×{sel.estLots} lots{sel.projected ? ' — strikes re-optimised when it fires' : ''}
                  </Typography>
                  <Tooltip arrow title="Save THIS tranche's spread as a synthetic symbol (no order placed) — it appears in the Charts symbol pane for this underlying, chartable with its own BUY/SHORT triggers.">
                    <span><Button size="small" variant="outlined" color="secondary" disabled={savingStage === sel.index || !sel.basket?.legs?.length} onClick={() => saveStageAsSymbol(sel)} sx={{ py: 0, fontSize: '0.62rem' }}>
                      {savingStage === sel.index ? 'Saving…' : '💾 Save as symbol'}
                    </Button></span>
                  </Tooltip>
                </Stack>
                <LegsTable legs={newLegs} />
                {sel.projected && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.25, mb: 0.5, fontStyle: 'italic' }}>
                  Entry ₹ are Black-Scholes estimates of each option's value when price reaches {fmtPx(sel.triggerPrice)} (holding time-to-expiry the same as now — no one knows when, or if, the level is hit). Actual fills use the live chain when the tranche deploys.
                </Typography>}

                {chartBasket?.payoffData && <Box sx={{ mt: 0.5 }}><PayoffChartPro basket={chartBasket} spotPrice={chartSpot} underlyingSymbol={(symbol || '').replace('USD', '')} hideHeader /></Box>}
                {tabIdx === stages.length - 1 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{combinedNote(view)}</Typography>}
              </Box>
            )}
            </>)}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mr: 'auto' }}>
          <ToggleButton value="PAPER" sx={{ textTransform: 'none', px: 1.5 }}>Paper</ToggleButton>
          <ToggleButton value="LIVE" color="error" sx={{ textTransform: 'none', px: 1.5 }}>Live</ToggleButton>
        </ToggleButtonGroup>
        <Button onClick={onClose} disabled={placing}>Cancel</Button>
        <Button variant="contained" color={mode === 'LIVE' ? 'error' : 'primary'} disabled={placing || !preview} onClick={place}
          startIcon={placing ? <CircularProgress size={15} color="inherit" /> : undefined}>
          {placing ? 'Placing…' : `Place ${mode} campaign`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Metric: React.FC<{ label: React.ReactNode; value: React.ReactNode; color?: string; hint?: string }> = ({ label, value, color, hint }) => (
  <Tooltip arrow title={hint || ''} disableHoverListener={!hint}>
    <Box sx={{ px: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color }}>{value}</Typography>
    </Box>
  </Tooltip>
);

export default StagedCampaignDialog;
